import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { audioService, type AudioSettings } from '../services/audio.service';

interface AudioState extends AudioSettings {
  // Actions
  setMasterVolume: (volume: number) => void;
  setSfxVolume: (volume: number) => void;
  setMusicVolume: (volume: number) => void;
  toggleSfx: () => void;
  toggleMusic: () => void;
  initAudio: () => void;
}

export const useAudioStore = create<AudioState>()(
  persist(
    (set) => ({
      // Initial state from audio service
      ...audioService.getSettings(),

      setMasterVolume: (volume: number) => {
        const clamped = Math.max(0, Math.min(1, volume));
        audioService.updateSettings({ masterVolume: clamped });
        set({ masterVolume: clamped });
      },

      setSfxVolume: (volume: number) => {
        const clamped = Math.max(0, Math.min(1, volume));
        audioService.updateSettings({ sfxVolume: clamped });
        set({ sfxVolume: clamped });
      },

      setMusicVolume: (volume: number) => {
        const clamped = Math.max(0, Math.min(1, volume));
        audioService.updateSettings({ musicVolume: clamped });
        set({ musicVolume: clamped });
      },

      toggleSfx: () => {
        const newState = audioService.toggleSfx();
        set({ sfxEnabled: newState });
      },

      toggleMusic: () => {
        const newState = audioService.toggleMusic();
        set({ musicEnabled: newState });
      },

      initAudio: () => {
        audioService.init();
        // Sync store with service
        const settings = audioService.getSettings();
        set(settings);
      },
    }),
    {
      name: 'after5pm-audio',
      onRehydrateStorage: () => (state) => {
        // Sync audio service with persisted state
        if (state) {
          audioService.updateSettings({
            masterVolume: state.masterVolume,
            sfxVolume: state.sfxVolume,
            musicVolume: state.musicVolume,
            sfxEnabled: state.sfxEnabled,
            musicEnabled: state.musicEnabled,
          });
        }
      },
    }
  )
);

// Convenience hook
export const useAudio = () => {
  const store = useAudioStore();
  return {
    ...store,
    play: (sound: Parameters<typeof audioService.play>[0]) => {
      audioService.init();
      audioService.play(sound);
    },
    startMusic: (style?: Parameters<typeof audioService.startMusic>[0]) => {
      audioService.init();
      audioService.startMusic(style);
    },
    stopMusic: () => audioService.stopMusic(),
  };
};
